/**
 * Plain-language help for call routing, shared by the web app (and later Leland):
 * names for numbers, summaries of what a feature does, warnings about dead ends, hours,
 * the next free number, and a greeting drafted from a menu.
 *
 * PBX-neutral and browser-safe: no Node APIs, no vendor field names.
 */
import { AGENT_FEATURE_SET, CATALOG, MAILBOX_FEATURE_SET, internalRouteLabel, isInternalRoute, parseMenu, type AccountKind, type Directory, type DirectoryEntry, type FieldValues } from './catalog.js';
import { holidayWhen, parseHolidays, upcoming } from './holidays.js';

// --- names for numbers -----------------------------------------------------------------------

/** "mailbox": an extension with the Voicemail only feature set (a voicemail box, not a person). */
export type NumberKind = AccountKind | 'mailbox' | 'other' | 'voicemail' | 'external' | 'unknown';

export interface NumberInfo {
  kind: NumberKind;
  /** What people call it: "Dana Ruiz", "Dana Ruiz's voicemail", "+1 410 555 0111". */
  name: string;
  number: string;
}

const KIND_WORD: Record<AccountKind, string> = {
  person: 'Extension',
  ringGroup: 'Ring group',
  callQueue: 'Call queue',
  autoAttendant: 'Auto attendant',
  timeRule: 'Time of day rule',
  callPark: 'Park orbit',
  phone: 'Phone',
  number: 'Phone number',
  conference: 'Conference room'
};

/** A phone number outside the tenant: "+..." or 7 digits or more. */
export function isExternal(value: string): boolean {
  return /^\+\d{3,}$/.test(value) || /^\d{7,}$/.test(value);
}

export function lookup(dir: Directory | undefined, value: string | undefined): NumberInfo {
  const v = (value ?? '').trim();
  if (!v) return { kind: 'unknown', name: '', number: '' };
  const entry = dir?.entries.find((e) => e.extension === v);
  if (entry?.kind === 'person' && entry.featureSet === MAILBOX_FEATURE_SET) return { kind: 'mailbox', name: entry.name || `Voicemail box ${v}`, number: v };
  if (entry) return { kind: entry.kind, name: entry.name || (entry.kind === 'other' ? `${capitalize(entry.label ?? 'number')} ${v}` : `${KIND_WORD[entry.kind]} ${v}`), number: v };
  const prefix = dir?.mailboxPrefix ?? '';
  if (prefix && v.startsWith(prefix) && v.length > prefix.length) {
    const owner = dir?.entries.find((e) => e.kind === 'person' && e.extension === v.slice(prefix.length));
    if (owner) return { kind: 'voicemail', name: `${owner.name || `Extension ${owner.extension}`}'s voicemail`, number: v };
  }
  if (isInternalRoute(v)) return { kind: 'external', name: internalRouteLabel(v), number: v };
  if (isExternal(v)) return { kind: 'external', name: v, number: v };
  return { kind: 'unknown', name: v, number: v };
}

export function nameOf(dir: Directory | undefined, value: string | undefined): string {
  return lookup(dir, value).name;
}

/** "Dana Ruiz", "Dana Ruiz and Marcus Bell", "Dana, Marcus and 3 more" style lists. */
export function joinNames(names: readonly string[], max = 4): string {
  const n = names.filter(Boolean);
  if (n.length <= 1) return n[0] ?? '';
  if (n.length > max) return `${n.slice(0, max - 1).join(', ')} and ${n.length - (max - 1)} more`;
  return `${n.slice(0, -1).join(', ')} and ${n[n.length - 1]}`;
}

const list = (v: string | undefined) => (v ?? '').split(/[\s,;]+/).filter(Boolean);
const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

// --- time ------------------------------------------------------------------------------------

/** "20 seconds", "5 minutes", "2 minutes 30 seconds". */
export function duration(secondsText: string | undefined): string {
  const n = Number(secondsText);
  if (!secondsText || !Number.isFinite(n)) return '';
  if (n < 120 && n % 60 !== 0) return `${n} second${n === 1 ? '' : 's'}`;
  const m = Math.floor(n / 60);
  const s = n % 60;
  return `${m} minute${m === 1 ? '' : 's'}${s ? ` ${s} seconds` : ''}`;
}

/** One opening, in minutes from midnight (to may be 1440 = midnight at the end of the day). */
export interface Interval {
  from: number;
  to: number;
}

export const DAY_FIELDS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
export type DayField = (typeof DAY_FIELDS)[number];
const DAY_SHORT: Record<DayField, string> = { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' };

const TIME = /^(\d{1,2})(?::(\d{2}))?(?::\d{2})?\s*([ap])?m?$/i;

function parseTime(t: string, end: boolean): number | null {
  const m = TIME.exec(t.trim());
  if (!m) return null;
  let h = Number(m[1]);
  const min = Number(m[2] ?? 0);
  if (min > 59) return null;
  const ap = m[3]?.toLowerCase();
  if (ap) {
    if (h < 1 || h > 12) return null;
    h = (h % 12) + (ap === 'p' ? 12 : 0);
  } else if (h > 24) return null;
  const total = h * 60 + min;
  if (total > 1440) return null;
  // "12:00A" (or 0:00) as an end time means midnight at the end of the day.
  return end && total === 0 ? 1440 : total;
}

/** "9:00A-12:00P 1:00P-5:00P" -> intervals; "" -> []; null when it can't be read. */
export function parseHours(value: string | undefined): Interval[] | null {
  const v = (value ?? '').trim();
  if (!v) return [];
  const out: Interval[] = [];
  for (const range of v.split(/\s+/)) {
    const [a, b, extra] = range.split('-');
    if (a === undefined || b === undefined || extra !== undefined) return null;
    const from = parseTime(a, false);
    const to = parseTime(b, true);
    if (from === null || to === null || to <= from) return null;
    out.push({ from, to });
  }
  return out;
}

/** Minutes -> "9:00A" (the format the PBX documents). */
export function pbxTime(min: number): string {
  const m = min % 1440;
  const h = Math.floor(m / 60);
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m % 60).padStart(2, '0')}${h < 12 ? 'A' : 'P'}`;
}

export function formatHours(intervals: readonly Interval[]): string {
  return intervals.map((i) => `${pbxTime(i.from)}-${pbxTime(i.to)}`).join(' ');
}

/** Minutes -> "9 AM", "9:30 AM", "noon", "midnight". */
export function clock(min: number): string {
  if (min === 720) return 'noon';
  if (min === 0 || min === 1440) return 'midnight';
  const h = Math.floor(min / 60);
  const h12 = h % 12 === 0 ? 12 : h % 12;
  const mm = min % 60;
  return `${h12}${mm ? `:${String(mm).padStart(2, '0')}` : ''} ${h < 12 ? 'AM' : 'PM'}`;
}

function dayText(value: string | undefined): string | null {
  const iv = parseHours(value);
  if (iv === null) return (value ?? '').trim();
  if (iv.length === 0) return null;
  return iv.map((i) => (i.from === 0 && i.to === 1440 ? 'all day' : `${clock(i.from)}–${clock(i.to)}`)).join(', ');
}

/** "Mon–Fri 9 AM–5 PM, Sat 10 AM–2 PM", "every day 9 AM–5 PM", "24/7", or "" for no hours. */
export function weekSummary(values: FieldValues): string {
  const texts = DAY_FIELDS.map((d) => dayText(values[d]));
  if (texts.every((t) => t === 'all day')) return '24/7';
  if (texts.every((t) => t !== null && t === texts[0])) return `every day ${texts[0]}`;
  const parts: string[] = [];
  let i = 0;
  while (i < 7) {
    const t = texts[i];
    let j = i;
    while (j + 1 < 7 && texts[j + 1] === t) j++;
    if (t !== null) parts.push(`${DAY_SHORT[DAY_FIELDS[i]!]}${j > i ? `–${DAY_SHORT[DAY_FIELDS[j]!]}` : ''} ${t}`);
    i = j + 1;
  }
  return parts.join(', ');
}

// --- summaries -----------------------------------------------------------------------------

/** Today as YYYY-MM-DD in the viewer's time zone. */
function todayText(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const ORDER_WORDS: Record<string, string> = {
  idle: 'whoever has been free longest',
  order: 'agents in the order of the list',
  random: 'agents at random',
  skills: 'the agent whose skills match best'
};

/** Sentences describing what a feature does, for tables and live previews. */
export function summarize(kind: AccountKind, v: FieldValues, dir?: Directory, today = todayText()): string[] {
  const names = (value: string | undefined) => joinNames(list(value).map((n) => nameOf(dir, n)));
  const afterHours = () =>
    v.afterHoursRule && v.afterHoursDestination
      ? [`While "${nameOf(dir, v.afterHoursRule)}" is on, calls go to ${nameOf(dir, v.afterHoursDestination)}.`]
      : [];
  switch (kind) {
    case 'person':
    case 'phone':
    case 'conference':
      return [];
    case 'number':
      return v.destination ? [`Calls go to ${nameOf(dir, v.destination)}.`] : ['Calls to it go nowhere yet.'];
    case 'ringGroup': {
      const stages = [1, 2, 3]
        .map((n) => ({ who: names(v[`stage${n}`]), secs: duration(v[`stage${n}Seconds`]) }))
        .filter((s) => s.who);
      const out: string[] = [];
      if (stages.length === 0) out.push('Nobody rings yet.');
      else out.push(stages.map((s, i) => `${i === 0 ? 'Rings' : 'then'} ${s.who}${s.secs ? ` for ${s.secs}` : ''}`).join(', ') + '.');
      if (v.ringStyle === 'idle' && stages.length) out.push('In each step, only whoever has been free longest rings.');
      if (v.finalDestination) out.push(`If nobody answers: ${nameOf(dir, v.finalDestination)}.`);
      // Only what differs from how ring groups usually behave.
      if (v.ringBusy === 'false') out.push('People already on a call are skipped.');
      if (v.ringMobiles === 'false') out.push('Mobile phones don’t ring.');
      if (v.missedCallEmail) out.push(`Missed calls are emailed to ${v.missedCallEmail}.`);
      if (v.recordCalls === 'true') out.push('Calls are recorded.');
      return [...out, ...afterHours()];
    }
    case 'callQueue': {
      const agents = list(v.agents);
      const out = [agents.length ? `${agents.length === 1 ? 'Agent' : `${agents.length} agents`}: ${names(v.agents)}.` : 'No agents yet.'];
      const order = ORDER_WORDS[v.ringOrder ?? ''];
      const wrap = Number(v.wrapUpSeconds) > 0 ? duration(v.wrapUpSeconds) : '';
      if (order) out.push(`Calls go to ${order}${wrap ? `, with a ${wrap} break after each call` : ''}.`);
      else if (wrap) out.push(`Agents get ${wrap} after each call before the next one.`);
      if (v.ringBusyAgents === 'true') out.push('Agents already on a call still get calls.');
      const tells = [v.announcePosition === 'true' ? 'their place in line' : '', v.announceWait === 'true' ? 'the expected wait' : ''].filter(Boolean);
      if (v.greeting) out.push(`Callers hear a welcome message${tells.length ? ` and ${tells.join(' and ')}` : ''}.`);
      else if (tells.length) out.push(`Callers hear ${tells.join(' and ')}.`);
      if (v.maxCallers) out.push(`Up to ${v.maxCallers} callers wait at once.`);
      if (v.maxWaitSeconds && v.maxWaitDestination) out.push(`After ${duration(v.maxWaitSeconds)}, callers go to ${nameOf(dir, v.maxWaitDestination)}.`);
      if (v.logoutAtMidnight === 'true') out.push('Everyone is signed out at midnight.');
      return out;
    }
    case 'autoAttendant': {
      const options = parseMenu(v.menu ?? '');
      const out: string[] = [];
      if (v.greetingRecording) out.push('Answers with a recorded greeting.');
      if (options.length) out.push(`${options.map((o) => `${o.key}: ${nameOf(dir, o.destination) || '?'}`).join(' · ')}.`);
      else out.push('No menu options: callers can dial an extension.');
      if (v.dialByName) out.push(`Press ${v.dialByName} to find someone by name.`);
      if (v.timeoutDestination) out.push(`No key${v.timeoutSeconds ? ` within ${duration(v.timeoutSeconds)}` : ''}: ${nameOf(dir, v.timeoutDestination)}.`);
      return [...out, ...afterHours()];
    }
    case 'timeRule': {
      const out: string[] = [];
      if (v.mode === 'manual') out.push(`Switched by hand: ${v.manualState === 'on' ? 'on' : 'off'}.`);
      else {
        const week = weekSummary(v);
        if (!week) out.push('No hours set, so it never switches by itself.');
        else out.push(v.hoursMeaning === 'on' ? `On ${week}.` : `Open ${week}. On outside those hours.`);
        const hol = holidaySummary(v.holidays, v.hoursMeaning === 'on', today);
        if (hol) out.push(hol);
      }
      if (v.pin) out.push('Can be switched by phone with a PIN.');
      return out;
    }
    case 'callPark': {
      if (!v.reminderSeconds) return ['No reminder.'];
      return [`Reminds whoever parked the call after ${duration(v.reminderSeconds)}${v.reminderDestination ? `, then sends it to ${nameOf(dir, v.reminderDestination)}` : ''}.`];
    }
  }
}

/** "Closed on 3 holidays. Next: Thanksgiving, Nov 26." */
export function holidaySummary(value: string | undefined, ruleOnDuringHours: boolean, today = todayText()): string {
  if (!value) return '';
  if (value.startsWith('raw:')) return 'Holidays are set in the phone system.';
  const all = parseHolidays(value) ?? [];
  const next = upcoming(all, today);
  if (!next.length) return '';
  const first = next[0]!;
  const when = holidayWhen({ ...first.holiday, from: first.on, to: first.on });
  const n = next.length;
  const lead = ruleOnDuringHours
    ? `${n} special day${n === 1 ? '' : 's'} coming up.`
    : next.every((x) => !x.holiday.hours)
      ? `Closed on ${n} upcoming holiday${n === 1 ? '' : 's'}.`
      : `${n} holiday${n === 1 ? '' : 's'} coming up, some with special hours.`;
  return `${lead} Next: ${first.holiday.name || 'a holiday'}, ${when}.`;
}

/** Helpful things that will happen as a result, shown alongside the preview. Never blocking. */
export function notes(kind: AccountKind, v: FieldValues, dir?: Directory): string[] {
  const out: string[] = [];
  if (kind === 'callQueue' && dir) {
    const switching = list(v.agents)
      .map((a) => dir.entries.find((e) => e.extension === a && e.kind === 'person'))
      .filter((e): e is DirectoryEntry => Boolean(e) && e!.featureSet !== undefined && e!.featureSet !== AGENT_FEATURE_SET);
    if (switching.length) {
      out.push(
        `${joinNames(switching.map((e) => e.name || `Extension ${e.extension}`))} ${switching.length === 1 ? 'will be switched' : 'will be switched'} to the Call queue agent feature set, so ${switching.length === 1 ? 'they' : 'they all'} can take queue calls.`
      );
    }
  }
  return out;
}

// --- warnings ------------------------------------------------------------------------------

/** Things that probably aren't what was meant. Never blocking. */
export function warnings(kind: AccountKind, v: FieldValues, dir?: Directory): string[] {
  const out: string[] = [];
  const dead = (value: string | undefined, where: string) => {
    const info = lookup(dir, value);
    if (dir && info.kind === 'unknown' && info.number) out.push(`${where} goes to ${info.number}, which isn't a number in your account.`);
    if (info.kind === 'timeRule') out.push(`${where} goes to a time of day rule. Calling a rule switches it; it doesn't ring anyone.`);
  };
  const afterHours = () => {
    if (v.afterHoursRule && !v.afterHoursDestination) out.push('After hours: choose where calls go while the rule is on.');
    if (!v.afterHoursRule && v.afterHoursDestination) out.push('After hours: choose the time of day rule that decides when this applies.');
    if (v.afterHoursDestination) dead(v.afterHoursDestination, 'After hours');
  };
  switch (kind) {
    case 'ringGroup':
      if (![v.stage1, v.stage2, v.stage3].some((s) => list(s).length)) out.push('Nobody will ring. Add at least one person.');
      for (const [n, label] of [
        ['stage1', 'First ring'],
        ['stage2', 'Second ring'],
        ['stage3', 'Third ring']
      ] as const) {
        for (const m of list(v[n])) dead(m, label);
      }
      if (!v.finalDestination) out.push("If nobody answers, the call keeps ringing until the caller hangs up. Consider sending it to someone's voicemail.");
      else dead(v.finalDestination, 'If nobody answers, the call');
      afterHours();
      break;
    case 'callQueue':
      if (!list(v.agents).length) out.push('Nobody will answer this queue. Add at least one agent.');
      for (const a of list(v.agents)) {
        const info = lookup(dir, a);
        if (dir && info.kind !== 'person') out.push(`Agent ${a} isn't a person's extension.`);
      }
      if (v.maxWaitSeconds && !v.maxWaitDestination) out.push('Choose where callers go after the longest wait.');
      if (v.maxWaitDestination) dead(v.maxWaitDestination, 'Overflow');
      if (v.ringOrder === 'skills') out.push('“By skill” only works once agents’ skills are set up. Ask Stitch to do that.');
      if (v.minAgents && Number(v.minAgents) > list(v.agents).length) out.push(`${v.minAgents} agents must stay signed in, but the queue has ${list(v.agents).length}.`);
      break;
    case 'autoAttendant': {
      const options = parseMenu(v.menu ?? '');
      for (const o of options) dead(o.destination, `Key ${o.key}`);
      if (options.length && !v.greeting) out.push('Add a greeting so callers know which key to press. "Write it for me" can draft one.');
      if (!options.length && !v.timeoutDestination) out.push("With no menu and nowhere to go without a key press, callers have to know an extension.");
      if (v.timeoutDestination) dead(v.timeoutDestination, 'No key pressed');
      if (v.dialByName && options.some((o) => o.key === v.dialByName)) out.push(`Key ${v.dialByName} is both a menu option and “find someone by name”. Choose a different key for one of them.`);
      afterHours();
      break;
    }
    case 'timeRule':
      if (v.mode !== 'manual' && !weekSummary(v)) out.push('With no hours, this rule never switches by itself.');
      for (const d of DAY_FIELDS) if (parseHours(v[d]) === null) out.push(`${DAY_SHORT[d]}: "${v[d]}" isn't a time range like 9:00A-5:00P.`);
      break;
    case 'callPark':
      if (v.reminderDestination && !v.reminderSeconds) out.push('Set a reminder time, or the call never goes to the reminder destination.');
      if (v.reminderDestination) dead(v.reminderDestination, 'After the reminder, the call');
      break;
    case 'person':
      break;
  }
  return out;
}

// --- numbers and greetings -----------------------------------------------------------------

/** Where each kind's numbers usually live, so new ones are easy to recognise. */
export const NUMBER_RANGES: Readonly<Record<Exclude<AccountKind, 'person' | 'phone' | 'number'>, readonly [number, number][]>> = {
  callPark: [
    [80, 89],
    [800, 849]
  ],
  ringGroup: [[300, 399]],
  callQueue: [[500, 599]],
  timeRule: [[600, 699]],
  autoAttendant: [[700, 799]],
  // Stitch's own hidden presence room only (1.12.18): never suggested.
  conference: []
};

/** The first number in the kind's usual range that nothing uses yet, or "" if the range is full. */
export function nextFreeNumber(kind: Exclude<AccountKind, 'person' | 'phone' | 'number'>, used: Iterable<string>, mailboxPrefix = '', length?: number): string {
  const taken = new Set(used);
  // A company whose extensions are longer (2986): the same ranges with as many digits (300 → 3000 to 3999).
  const ranges = NUMBER_RANGES[kind].map(([lo, hi]): [number, number] => {
    const grow = length && String(lo).length < length && String(hi).length === String(lo).length ? 10 ** (length - String(lo).length) : 1;
    return [lo * grow, (hi + 1) * grow - 1];
  });
  for (const [lo, hi] of ranges) {
    for (let n = lo; n <= hi; n++) {
      const s = String(n);
      // Skip numbers that look like someone's voicemail (8 + 201 = 8201).
      if (mailboxPrefix && s.startsWith(mailboxPrefix) && taken.has(s.slice(mailboxPrefix.length))) continue;
      if (!taken.has(s)) return s;
    }
  }
  return '';
}

/** Where each kind starts when a tenant has none yet. */
export const FIRST_NUMBER: Readonly<Record<AccountKind, number>> = {
  person: 200,
  ringGroup: 300,
  callQueue: 500,
  timeRule: 600,
  autoAttendant: 700,
  callPark: 80,
  // Phones are known by their MAC address, and phone numbers are the number itself.
  phone: 0,
  number: 0,
  // Stitch's own hidden presence room (1.12.18): never suggested to anyone.
  conference: 66000
};

/**
 * The number to suggest for something new: the one after the last (highest) number already
 * used for that kind, so new people follow on from the last extension and new ring groups from
 * the last ring group. Numbers in use are skipped, and so are numbers that would clash with
 * voicemail (8 + 201 dials 201's voicemail, so 8201 is never suggested while 201 exists, and a
 * person is never suggested at 201 while something already sits at 8201). With none of the kind
 * yet, the kind's usual starting number.
 */
export function suggestNumber(
  kind: AccountKind,
  entries: readonly Pick<DirectoryEntry, 'extension' | 'kind'>[],
  opts: { mailboxPrefix?: string; alsoUsed?: Iterable<string>; length?: number } = {}
): string {
  const taken = new Set([...entries.map((e) => e.extension), ...(opts.alsoUsed ?? [])]);
  const prefix = opts.mailboxPrefix ?? '';
  // The company's extension length, when it has one (2986): only numbers with that many digits.
  const length = opts.length;
  const fits = (s: string) => !length || s.length === length;
  const mine = entries.filter((e) => e.kind === kind && /^\d{1,9}$/.test(e.extension) && fits(e.extension)).map((e) => Number(e.extension));
  const first = FIRST_NUMBER[kind];
  // A kind's usual first number, with as many digits as the company's (300 → 3000 for four).
  const scaled = length && first > 0 && String(first).length < length ? first * 10 ** (length - String(first).length) : first;
  const free = (s: string) => !taken.has(s) && !(prefix && s.startsWith(prefix) && taken.has(s.slice(prefix.length))) && !(prefix && kind === 'person' && taken.has(`${prefix}${s}`));
  const starts = [mine.length ? Math.max(...mine) + 1 : scaled, ...(length ? [10 ** (length - 1)] : [])];
  for (const start of starts) {
    for (let n = start; n < start + 10_000; n++) {
      const s = String(n);
      if (length && s.length > length) break;
      if (fits(s) && free(s)) return s;
    }
  }
  return '';
}

/** A greeting drafted from an auto attendant's menu. */
export function greetingFor(menu: string, dir: Directory | undefined, company: string): string {
  const lines = [`Thank you for calling ${company}.`];
  for (const o of parseMenu(menu)) {
    const info = lookup(dir, o.destination);
    if (!o.key || !info.name) continue;
    if (info.kind === 'person') lines.push(`To reach ${info.name}, press ${o.key}.`);
    else if (info.kind === 'voicemail') lines.push(`To leave a message for ${info.name.replace(/'s voicemail$/, '')}, press ${o.key}.`);
    else if (info.kind === 'mailbox') lines.push(`To leave a message for ${info.name}, press ${o.key}.`);
    else lines.push(`For ${info.name}, press ${o.key}.`);
  }
  lines.push("If you know your party's extension, you can dial it at any time.");
  return lines.join(' ');
}

/** A welcome message for a call queue. */
export function queueGreetingFor(name: string): string {
  return `Thank you for calling. All of our ${name ? `${name} ` : ''}team members are helping other callers. Please stay on the line and the next available person will be right with you.`;
}

/** Entries a destination can be: everything that rings or answers (not time of day rules). */
export function destinations(dir: Directory | undefined): DirectoryEntry[] {
  return (dir?.entries ?? []).filter((e) => e.kind !== 'timeRule');
}


// --- who uses a number ---------------------------------------------------------------------------

/** One account with its settings, as routing references are read from. */
export interface RoutingAccount {
  kind: AccountKind;
  extension: string;
  values: FieldValues;
}

export interface Reference {
  /** The account that uses the number. */
  kind: AccountKind;
  extension: string;
  name: string;
  field: string;
  /**
   * route: calls are sent there (a menu key, a no-answer or after-hours destination, a time of
   * day rule). Removing the number would leave a dead end, so removal waits until it's changed.
   * member: it rings as one of several (ring group steps, queue agents). Removal takes it out.
   */
  how: 'route' | 'member';
  /** Where, in words: "key 3", "if nobody answers", "after hours". */
  where: string;
}

/** Short words for where a field sends calls. */
const WHERE: Record<string, string> = {
  finalDestination: 'if nobody answers',
  afterHoursRule: 'after hours rule',
  afterHoursDestination: 'after hours',
  maxWaitDestination: 'after the longest wait',
  timeoutDestination: 'when no key is pressed',
  reminderDestination: 'after the reminder',
  stage1: 'rings first',
  stage2: 'rings second',
  stage3: 'rings third',
  agents: 'agent'
};

/**
 * Every place another account sends calls to `number`, or to its voicemail (for a person).
 * Accounts listed in `ignore` (being removed in the same change, say) are skipped.
 */
export function referencesTo(
  number: string,
  accounts: readonly RoutingAccount[],
  opts: { mailboxPrefix?: string; ignore?: ReadonlySet<string> } = {}
): Reference[] {
  const targets = new Set([number, ...(opts.mailboxPrefix ? [`${opts.mailboxPrefix}${number}`] : [])]);
  const out: Reference[] = [];
  for (const a of accounts) {
    if (a.extension === number || opts.ignore?.has(a.extension)) continue;
    const name = displayName(a);
    for (const f of CATALOG[a.kind].fields) {
      const v = (a.values[f.name] ?? '').trim();
      if (!v) continue;
      const ref = (how: Reference['how'], where: string) => out.push({ kind: a.kind, extension: a.extension, name, field: f.name, how, where });
      if (f.type === 'destination' || f.type === 'rule') {
        if (targets.has(v)) ref('route', WHERE[f.name] ?? f.noun);
      } else if (f.type === 'members' && !f.live) {
        if (list(v).some((m) => targets.has(m))) ref('member', WHERE[f.name] ?? f.noun);
      } else if (f.type === 'menu') {
        for (const o of parseMenu(v)) if (targets.has(o.destination)) ref('route', `key ${o.key}`);
      }
    }
  }
  return out;
}

function displayName(a: RoutingAccount): string {
  const n = a.kind === 'person' ? `${a.values.firstName ?? ''} ${a.values.lastName ?? ''}`.trim() : (a.values.name ?? '').trim();
  return n || `${KIND_WORD[a.kind]} ${a.extension}`;
}

/** "Main Menu (key 3)", "Front Office (if nobody answers) and Support (agent)". */
export function describeReferences(refs: readonly Reference[]): string {
  return joinNames(refs.map((r) => `${r.name} (${r.where})`));
}

/** The menus that send a key to this auto attendant: it is their sub-menu. */
export function parentMenus(extension: string, accounts: readonly RoutingAccount[]): Reference[] {
  return referencesTo(extension, accounts).filter((r) => r.kind === 'autoAttendant' && r.field === 'menu');
}
